import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import {
  initializeTracking,
  trackPurchase,
  initializeFacebookPixelWithAM
} from '../services/tracking';
import logger from '../utils/logger';
import './Checkout.css';
import './ThankYou.css';

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const originDomain = searchParams.get('origin') || 'localhost';

  // Get event_id from URL (passed from checkout after successful payment)
  const eventIdFromUrl = searchParams.get('event_id');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [couponCopied, setCouponCopied] = useState(false);
  const [policies, setPolicies] = useState(null);

  // Fixed coupon code - created automatically in Shopify during store setup
  const couponCode = 'THANKYOU10';

  useEffect(() => {
    const loadOrderAndTrack = async () => {
      try {
        const orderData = await api.getOrder(orderId);
        setOrder(orderData);

        // Load store config for branding and tracking
        let configData = null;
        try {
          configData = await api.getCheckoutConfig(originDomain);

          // Apply branding colors
          if (configData.primary_color) {
            document.documentElement.style.setProperty('--primary-color', configData.primary_color);
          }
          if (configData.secondary_color) {
            document.documentElement.style.setProperty('--secondary-color', configData.secondary_color);
          }

          // Store policies for footer links
          if (configData.policies) {
            setPolicies(configData.policies);
          }
        } catch (err) {
          logger.warn('Could not load store config, using defaults:', err);
        }

        setLoading(false);

        // Fire tracking events only once per order
        if (!sessionStorage.getItem(`purchase_tracked_${orderId}`)) {
          // Get event_id: from URL, sessionStorage, or generate new
          const storedEventId = sessionStorage.getItem('purchase_event_id');
          const eventId = eventIdFromUrl || storedEventId || `purchase_${orderId}_${Date.now()}`;

          logger.debug('📊 ThankYou: Firing Purchase tracking with eventId:', eventId);

          // Initialize tracking pixels if config available
          if (configData?.tracking) {
            // Initialize FB Pixel with Advanced Matching using order customer data
            if (configData.tracking.facebook_pixel_id) {
              initializeFacebookPixelWithAM(configData.tracking.facebook_pixel_id, {
                email: orderData.customer_data?.email,
                phone: orderData.customer_data?.phone,
                firstName: orderData.customer_data?.first_name,
                lastName: orderData.customer_data?.last_name,
                city: orderData.shipping_address?.city,
                state: orderData.shipping_address?.state,
                zip: orderData.shipping_address?.zip,
                country: orderData.shipping_address?.country || 'US'
              });
            }

            // Initialize other pixels (GTM, Google Ads, TikTok, etc.)
            initializeTracking(
              configData.tracking,
              configData.custom_head_script,
              configData.custom_body_script
            );

            // Fire Purchase event with eventID for CAPI deduplication
            trackPurchase(
              configData.tracking,
              {
                order_id: orderId,
                transaction_id: orderData.transaction_id,
                total_amount: orderData.total_amount,
                shipping_cost: orderData.shipping_cost,
                tax_amount: orderData.tax_amount,
                items: orderData.items.map(item => ({
                  shopify_id: item.shopify_id || item.product_id,
                  product_id: item.product_id,
                  name: item.name,
                  price: item.price,
                  quantity: item.quantity
                }))
              },
              eventId, // CRITICAL: Same eventID used by server-side CAPI
              {
                email: orderData.customer_data?.email,
                phone: orderData.customer_data?.phone,
                firstName: orderData.customer_data?.first_name,
                lastName: orderData.customer_data?.last_name,
                city: orderData.shipping_address?.city,
                state: orderData.shipping_address?.state,
                zip: orderData.shipping_address?.zip,
                country: orderData.shipping_address?.country || 'US'
              }
            );
          }

          // Mark as tracked to prevent duplicate events
          sessionStorage.setItem(`purchase_tracked_${orderId}`, 'true');
          // Clean up stored event_id
          sessionStorage.removeItem('purchase_event_id');

          logger.debug('✅ ThankYou: Purchase tracking completed');
        } else {
          logger.debug('ℹ️ ThankYou: Purchase already tracked for this order');
        }
      } catch (err) {
        console.error('Error loading order:', err);
        setError(err.message || 'Failed to load order details');
        setLoading(false);
      }
    };

    if (orderId) {
      loadOrderAndTrack();
    }
  }, [orderId, originDomain, eventIdFromUrl]);

  const handleCopyCoupon = () => {
    navigator.clipboard.writeText(couponCode);
    setCouponCopied(true);
    setTimeout(() => setCouponCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="thank-you-page">
        <div className="thank-you-container-new">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading your order...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="thank-you-page">
        <div className="thank-you-container-new">
          <div className="error-page">
            <h1>Oops!</h1>
            <p>{error || 'Order not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="thank-you-page">
      <div className="thank-you-container-new">
        {/* Left Column - Confirmation Details */}
        <div className="thank-you-left">
          {/* Success Header */}
          <div className="confirmation-header">
            <div className="checkmark-circle">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="confirmation-text-header">
              <p className="confirmation-id">Confirmation #{orderId.slice(0, 8).toUpperCase()}</p>
              <h1 className="thank-you-title">Thank you, {order.customer_data.first_name || 'valued customer'}!</h1>
            </div>
          </div>

          {/* Coupon Card - Highlighted */}
          <div className="coupon-card">
            <div className="coupon-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 7h-4V5c0-.6-.4-1-1-1H9c-.6 0-1 .4-1 1v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z"/>
                <circle cx="12" cy="15" r="2"/>
              </svg>
              <span>Your next purchase</span>
            </div>
            <h2 className="coupon-title">Get 10% OFF</h2>
            <p className="coupon-description">Use this exclusive coupon on your next order</p>

            <div className="coupon-code-wrapper">
              <div className="coupon-code">
                <span className="coupon-code-text">{couponCode}</span>
                <button
                  className="copy-button"
                  onClick={handleCopyCoupon}
                  aria-label="Copy coupon code"
                >
                  {couponCopied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13 4L6 11L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <rect x="5" y="5" width="9" height="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M3 11V2h9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <p className="coupon-expiry">Valid for 30 days • No minimum purchase</p>
          </div>

          {/* Order Confirmation Message */}
          <div className="confirmation-message">
            <p className="confirmation-primary">Your order is confirmed</p>
            <p className="confirmation-secondary">You'll receive a confirmation email soon</p>
          </div>

          {/* Order Details Section - Table Format */}
          <div className="order-details-section">
            <h3 className="section-title">Order details</h3>

            <div className="order-details-table">
              {/* Row 1: Contact Information & Payment Method */}
              <div className="detail-row-2col">
                <div className="detail-col">
                  <h4 className="detail-label">Contact information</h4>
                  <p className="detail-value">{order.customer_data.email}</p>
                </div>
                <div className="detail-col">
                  <h4 className="detail-label">Payment method</h4>
                  <div className="payment-method-inline">
                    <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                      <rect width="32" height="20" rx="3" fill="#E5E7EB"/>
                      <rect x="2" y="14" width="8" height="2" rx="1" fill="#9CA3AF"/>
                    </svg>
                    <p className="detail-value">Credit card</p>
                  </div>
                </div>
              </div>

              {/* Row 2: Shipping Address & Billing Address */}
              <div className="detail-row-2col">
                <div className="detail-col">
                  <h4 className="detail-label">Shipping address</h4>
                  <div className="address-block">
                    <p>{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                    <p>{order.shipping_address.address}</p>
                    <p>{order.shipping_address.city}</p>
                    <p>{order.shipping_address.zip}</p>
                    <p>Peru</p>
                    <p>{order.customer_data.phone || '+51 1 6177148'}</p>
                  </div>
                </div>
                <div className="detail-col">
                  <h4 className="detail-label">Billing address</h4>
                  <div className="address-block">
                    <p>{order.billing_address?.first_name || order.shipping_address.first_name} {order.billing_address?.last_name || order.shipping_address.last_name}</p>
                    <p>{order.billing_address?.address || order.shipping_address.address}</p>
                    <p>{order.billing_address?.city || order.shipping_address.city}</p>
                    <p>{order.billing_address?.zip || order.shipping_address.zip}</p>
                    <p>Peru</p>
                    <p>{order.customer_data.phone || '+51 1 6177148'}</p>
                  </div>
                </div>
              </div>

              {/* Row 3: Shipping Method (full width) */}
              <div className="detail-row-full">
                <div className="detail-col">
                  <h4 className="detail-label">Shipping method</h4>
                  <p className="detail-value">{order.shipping_method || 'Standard Shipping'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="thank-you-footer">
            <div className="footer-links">
              {policies?.refund_policy?.url && (
                <a href={policies.refund_policy.url} target="_blank" rel="noopener noreferrer">
                  Refund policy
                </a>
              )}
              {policies?.shipping_policy?.url && (
                <a href={policies.shipping_policy.url} target="_blank" rel="noopener noreferrer">
                  Shipping policy
                </a>
              )}
              {policies?.privacy_policy?.url && (
                <a href={policies.privacy_policy.url} target="_blank" rel="noopener noreferrer">
                  Privacy policy
                </a>
              )}
              {policies?.terms_of_service?.url && (
                <a href={policies.terms_of_service.url} target="_blank" rel="noopener noreferrer">
                  Terms of service
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Order Summary */}
        <div className="thank-you-right">
          <div className="order-summary-sticky">
            {/* Product Items */}
            <div className="summary-items">
              {order.items.map((item, index) => {
                // OrderSerializer returns items with flat structure
                const productImage = item.image || null;
                const productName = item.name || 'Product';
                const itemTotal = (parseFloat(item.price) || 0) * (item.quantity || 1);

                return (
                  <div key={index} className="summary-item">
                    <div className="summary-item-image">
                      <div className="summary-item-quantity">{item.quantity}</div>
                      {productImage && (
                        <img
                          src={productImage}
                          alt={productName}
                          className="summary-item-thumbnail"
                        />
                      )}
                    </div>
                    <div className="summary-item-details">
                      <h4 className="summary-item-name">{productName}</h4>
                      {item.variant_title && (
                        <p className="summary-item-variant">
                          {item.variant_title}
                        </p>
                      )}
                    </div>
                    <div className="summary-item-price">
                      ${itemTotal.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Price Breakdown */}
            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>${parseFloat(order.subtotal).toFixed(2)}</span>
              </div>
              {/* Discount - after Subtotal, before Shipping (Shopify standard order) */}
              {order.total_discount && parseFloat(order.total_discount) > 0 && (
                <div className="summary-row discount-row">
                  <span>
                    Discount {order.discount_code_used && `(${order.discount_code_used})`}
                  </span>
                  <span className="discount-amount">
                    -${parseFloat(order.total_discount).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="summary-row">
                <span>Shipping</span>
                <span>${parseFloat(order.shipping_cost || 0).toFixed(2)}</span>
              </div>
              {order.tip_amount && parseFloat(order.tip_amount) > 0 && (
                <div className="summary-row">
                  <span>Tip</span>
                  <span>${parseFloat(order.tip_amount).toFixed(2)}</span>
                </div>
              )}
              {order.tax_amount && parseFloat(order.tax_amount) > 0 && (
                <div className="summary-row">
                  <span>Tax</span>
                  <span>${parseFloat(order.tax_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row summary-total">
                <span>Total</span>
                <span className="total-amount">
                  ${parseFloat(order.total_amount).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Continue Shopping Button */}
            <button
              className="continue-shopping-button"
              onClick={() => window.location.href = policies?.shop_url || '/'}
            >
              Continue shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
